import { TellescopeModelFromName, type SupportedSession } from "@tellescope/tanstack-query";
import {
  applyFieldResponseMetadata,
  dropStaleChoiceSelections,
  deriveComputedValueKey,
  resolveDatabasePrepopulation,
  resolveFormTranslationLanguages,
  subFieldIdsOf,
} from "../../utils";
import { useFormTranslations } from "../use-form-translations";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormTypesWithValues, TellescopeFieldResponse } from "../../types";
import {
  AnswerForType,
  DatabaseRecord,
  Enduser,
  FormResponseValue,
  FormResponseValueAnswer,
} from "@tellescope/types-models";
import { usePendingFileUploads } from "./use-pending-file-uploads";
import type { FormShallowStoreHook } from "../../store";
import { useSubmitPhase } from "./use-submit-phase";
import { useResolveCurrentResponses } from "./use-resolve-current-responses";
import { validateFieldValue } from "./validate-field-value";
import { useFormDataQueries } from "./use-form-data-queries";
import { useTouchedFields } from "./use-touched-fields";
import { useVisibleFields } from "./use-visible-fields";
import { useSaveResponse } from "./use-save-response";
import { isFormComplete } from "./is-form-complete";
import { useInterpolateFieldText } from "./use-interpolate-field-text";

export type LocalTellescopeFormResponseMap = Record<string, TellescopeFieldResponse>;

export interface UseTellescopeFormCoreArgs {
  formId: string;
  formTitle?: string;
  responseId?: string;
  enduserId: string;
  parentResponseId?: string;
  rootResponseId?: string;
  existingResponses?: FormResponseValue[];
  accessCode?: string;
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  urlLogicValue?: string;
  publicIdentifier?: string;
  useFormShallowStore: FormShallowStoreHook;
  session?: SupportedSession | null;
}

export interface ValidateFieldArgs<TField extends keyof AnswerForType = keyof AnswerForType> {
  fieldId: string;
  valueOverride?: AnswerForType[TField];
}

/**
 * Internal hook containing shared logic for both single-page and enduser form flows.
 * Not intended for direct consumption — use `useTellescopeSinglePageForm` or `useTellescopeEnduserForm`.
 */
export const useTellescopeFormCore = ({
  formId,
  formTitle: providedFormTitle,
  responseId,
  enduserId,
  parentResponseId,
  rootResponseId,
  existingResponses,
  accessCode,
  enduser,
  urlLogicValue: providedUrlLogicValue,
  publicIdentifier,
  useFormShallowStore,
  session,
}: UseTellescopeFormCoreArgs) => {
  const { t, i18n } = useTranslation("forms");
  const resolveCurrentResponses = useResolveCurrentResponses({ publicIdentifier });

  const {
    form,
    fields,
    response,
    formTitle,
    responsesMap,
    enduserForPrepopulation,
    formQuery,
    formFieldsQuery,
    responseQuery,
    enduserResponseInfoQuery,
    enduserQuery,
  } = useFormDataQueries({ formId, providedFormTitle, responseId, enduserId, accessCode, enduser });

  // The write target of setFieldValue and the top of the currentResponses precedence chain.
  const [unsavedResponses, setUnsavedResponses] = useState<LocalTellescopeFormResponseMap>({});
  const {
    touchedFields,
    setTouchedFields,
    repeatInputs,
    setRepeatValue,
    repeatTouchedFields,
    handleFieldBlur,
    handleRepeatBlur,
    markFieldTouched,
    untouchFields,
  } = useTouchedFields({ useFormShallowStore });

  const currentResponses = useMemo(
    () =>
      resolveCurrentResponses({
        fieldsArr: fields?.fieldsArr,
        unsavedResponses,
        responsesMap,
        existingResponses,
        enduserForPrepopulation,
        form,
      }),
    [
      fields?.fieldsArr,
      unsavedResponses,
      responsesMap,
      existingResponses,
      enduserForPrepopulation,
      form,
      resolveCurrentResponses,
    ]
  );

  // Set by whichever surface owns this store — the deeplink reads it from the query string. Falls
  // back to the argument for callers that still pass it directly.
  const { logic: storedUrlLogicValue } = useFormShallowStore("logic");

  const currentResponsesRef = useRef(currentResponses);
  currentResponsesRef.current = currentResponses;

  const { pendingFiles, setPendingFilesForField, uploadPendingFiles, uploadProgress } = usePendingFileUploads({
    fieldsMap: fields?.fieldsMap,
    currentResponses,
    enduserId,
    setUnsavedResponses,
    useFormShallowStore,
  });

  const { logicOptions, subFieldIds, isSubFieldHidden, visibleFields } = useVisibleFields({
    form,
    fieldsArr: fields?.fieldsArr,
    fieldsMap: fields?.fieldsMap,
    currentResponses,
    urlLogicValue: storedUrlLogicValue ?? providedUrlLogicValue,
    enduser: enduserQuery.data,
    useFormShallowStore,
  });

  const resolvedEnduser = accessCode ? enduser : enduserQuery.data;

  const translations = useFormTranslations({ form, language: i18n.language, accessCode, session });

  const validateField = useCallback(
    <TField extends keyof AnswerForType = keyof AnswerForType>({
      fieldId,
      valueOverride,
    }: ValidateFieldArgs<TField>): string | null => {
      const field = fields?.fieldsMap[fieldId];
      if (!field) return t("Field not found");
      return validateFieldValue({
        field,
        fieldsMap: fields?.fieldsMap,
        currentResponses,
        pendingFiles,
        repeatInputs,
        logicOptions,
        isSubFieldHidden,
        valueOverride,
        translations,
      });
    },
    [fields?.fieldsMap, currentResponses, pendingFiles, repeatInputs, logicOptions, isSubFieldHidden, translations, t]
  );

  const validateFieldValueById = useCallback((fieldId: string) => validateField({ fieldId }), [validateField]);

  const validateAllFields = useCallback((): boolean => {
    const fieldIds = visibleFields.flatMap((field) => [field.id, ...subFieldIdsOf(field)]);
    setTouchedFields(new Set(fieldIds));
    return fieldIds.every((fieldId) => validateFieldValueById(fieldId) === null);
  }, [visibleFields, setTouchedFields, validateFieldValueById]);

  const setFieldValue = useCallback(
    <TField extends FormTypesWithValues>(fieldId: string, value: AnswerForType[TField]) => {
      const field = fields?.fieldsMap[fieldId];
      const originalResponse = currentResponsesRef.current[fieldId] ?? {
        fieldId,
        fieldTitle: field?.title ?? "",
        answer: {
          type: field?.type,
          value: undefined,
        },
      };
      if (
        value !== undefined &&
        originalResponse?.answer.value !== undefined &&
        typeof value !== typeof originalResponse?.answer.value
      ) {
        return;
      }

      const newAnswer = {
        ...originalResponse?.answer,
        value,
      } as FormResponseValueAnswer;
      const newResponse: FormResponseValue = applyFieldResponseMetadata(
        {
          ...originalResponse,
          answer: newAnswer,
          computedValueKey: deriveComputedValueKey(field?.intakeField, newAnswer),
        },
        field
      );

      // Swept against the ref rather than inside the state updater: React only runs an updater
      // eagerly when no update is already queued, so reading the emptied ids after the call
      // dropped them — and the cleanup below — whenever an update was in flight.
      const merged = { ...currentResponsesRef.current, [fieldId]: newResponse };
      const swept = dropStaleChoiceSelections({
        responses: merged,
        fieldsArr: fields?.fieldsArr ?? [],
        changedFieldId: fieldId,
        logicOptions,
      });

      // Only the changed field and whatever the sweep rewrote: `merged` also holds saved and
      // prepopulated answers, which must not be promoted into unsaved state.
      const changed = Object.entries(swept.responses).reduce<LocalTellescopeFormResponseMap>(
        (acc, [id, response]) => (response === merged[id] && id !== fieldId ? acc : { ...acc, [id]: response }),
        {}
      );

      setUnsavedResponses((prev) => ({ ...prev, ...changed }));

      if (swept.emptiedFieldIds.size) untouchFields(swept.emptiedFieldIds);
    },
    [fields?.fieldsMap, fields?.fieldsArr, logicOptions, untouchFields]
  );

  const applyDatabasePrepopulation = useCallback(
    (records: DatabaseRecord[]) => {
      const prepopulated = resolveDatabasePrepopulation({
        fieldsArr: fields?.fieldsArr ?? [],
        records,
        currentResponses: currentResponsesRef.current,
      });
      if (!Object.keys(prepopulated).length) return;
      setUnsavedResponses((prev) => ({ ...prev, ...prepopulated }));
    },
    [fields?.fieldsArr]
  );

  const { customerId, setCustomerId, bookedAppointmentUrl, setBookedAppointmentUrl } = useFormShallowStore(
    "customerId",
    "setCustomerId",
    "bookedAppointmentUrl",
    "setBookedAppointmentUrl"
  );

  const { prepareFormResponseMutation, preparedResponse, submitFormMutation, saveFieldResponseMutation, saveResponse } =
    useSaveResponse({
      formId,
      enduserId,
      responseId,
      parentResponseId,
      rootResponseId,
      formTitle,
      response,
      currentResponses,
      enduser: resolvedEnduser,
      fieldsMap: fields?.fieldsMap,
    });

  const { submitPhase, runSubmit } = useSubmitPhase({ uploadPendingFiles, useFormShallowStore });

  const interpolateFieldText = useInterpolateFieldText({
    enduser: resolvedEnduser,
    currentResponses,
    translations,
  });

  const translationLanguages = useMemo(() => resolveFormTranslationLanguages(form), [form]);

  const isComplete = useMemo(
    () =>
      isFormComplete({
        visibleFields,
        fieldsMap: fields?.fieldsMap,
        currentResponses,
        validate: validateFieldValueById,
      }),
    [visibleFields, fields?.fieldsMap, currentResponses, validateFieldValueById]
  );

  return {
    form,
    formQuery,
    formFieldsQuery,
    responseQuery,
    enduser: resolvedEnduser,
    enduserResponseInfoQuery,
    formTitle,
    prepareFormResponseMutation,
    preparedResponse,
    submitFormMutation,
    saveFieldResponseMutation,
    currentResponses,
    responsesMap,
    interpolateFieldText,
    translations,
    translationLanguages,
    touchedFields,
    logicOptions,
    visibleFields,
    isComplete,
    validateField,
    validateFieldValue: validateFieldValueById,
    validateAllFields,
    setFieldValue,
    handleFieldBlur,
    markFieldTouched,
    saveResponse,
    fields,
    subFieldIds,
    isSubFieldHidden,
    repeatInputs,
    repeatTouchedFields,
    setRepeatValue,
    handleRepeatBlur,
    pendingFiles,
    setPendingFilesForField,
    applyDatabasePrepopulation,
    customerId,
    setCustomerId,
    bookedAppointmentUrl,
    setBookedAppointmentUrl,
    uploadPendingFiles,
    runSubmit,
    submitPhase,
    uploadProgress,
  };
};
